/**
 * ReportsAgGridHook
 *
 * One AG Grid hook for every monitoring report. Unlike the other ~19 grid hooks
 * in this app, nothing here is report-specific: the column definitions arrive
 * from the server as data, built from the report's `ReportingCore.Report.Meta`.
 * That is what lets a new report be a module plus a registry entry with no new
 * JavaScript — plan §4.5.
 *
 * Dataset attributes:
 *   data-columns   JSON [{key, label, type, hint}]  — from Meta.columns
 *   data-rows      JSON [ {..}, .. ]                — the filtered set (client only)
 *   data-row-model "client" | "server"
 *   data-empty     text for the no-rows overlay
 *
 * ## Two row models, one hook
 *
 * `client` ships the whole filtered set in `data-rows` and lets AG Grid sort,
 * filter, group and paginate it in the browser. Correct for the aggregate
 * reports, whose row count is bounded by their grouping.
 *
 * `server` registers a datasource instead: AG Grid asks for blocks as the user
 * scrolls and sends its sort and filter state with each request, which the
 * server executes in MySQL against the whole set. Required for the
 * one-row-per-transaction reports, which are unbounded by construction.
 *
 * The block request travels over the LiveView socket via `pushEvent` with a
 * reply, rather than to a JSON endpoint. The LiveView already holds the
 * report's own filters and the user's session, so a separate endpoint would
 * have to re-parse the filters and re-check the permission — two places to
 * disagree about what the user is allowed to see.
 *
 * ## Formatting mirrors ReportingCore.Format
 *
 * Values are sent raw, not pre-formatted, because AG Grid must sort and filter
 * on numbers — a column of "1,234.00" strings sorts lexically, which puts 9.00
 * after 1,234.00. So the grid formats for display here and the server formats
 * for CSV in `ReportingCore.Format`.
 *
 * That is a deliberate duplication, and the two must agree: money is two
 * decimals thousands-separated, integers are thousands-separated, percent is
 * one decimal with a trailing sign. `Intl.NumberFormat("en-US")` produces the
 * same output as `Format.separate_thousands/1` for both. If you change one
 * side, change the other.
 */

const money = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const whole = new Intl.NumberFormat("en-US");

const NUMERIC = ["integer", "money", "decimal", "percent"];

const formatters = {
  money: (p) => (p.value == null || p.value === "" ? "" : money.format(Number(p.value))),
  decimal: (p) =>
    p.value == null || p.value === "" ? "" : Number(p.value).toFixed(2),
  integer: (p) => (p.value == null || p.value === "" ? "" : whole.format(Number(p.value))),
  percent: (p) =>
    p.value == null || p.value === "" ? "" : `${Number(p.value).toFixed(1)}%`,
};

const BLOCK_SIZE = 100;

const ReportsAgGridHook = {
  mounted() {
    const columns = this.parse(this.el.dataset.columns, []);

    if (!window.agGrid || !window.agGrid.createGrid) {
      console.error("ReportsAgGridHook: agGrid not found on window");
      return;
    }

    this.serverSide = this.el.dataset.rowModel === "server";

    this.gridApi = window.agGrid.createGrid(this.el, {
      columnDefs: columns.map((c) => this.columnDef(c)),
      ...(this.serverSide ? this.serverSideOptions() : this.clientOptions()),
      domLayout: this.serverSide ? "normal" : "autoHeight",
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: true,
        minWidth: 110,
        flex: 1,
      },
      // Enterprise, and the reason a report grid beats a plain table: an
      // operator can group a daily breakdown by merchant or currency without
      // the server needing a second report for each shape.
      //
      // Client-side only. Grouping a server-side report would mean an
      // aggregate query shaped by the browser, which the datasource
      // deliberately does not accept — see ReportingCore.GridRequest.
      rowGroupPanelShow: this.serverSide ? "never" : "always",
      animateRows: true,
      suppressAggFuncInHeader: true,
      overlayNoRowsTemplate: `<span style="padding:20px;color:#6B7280;">${
        this.el.dataset.empty || "No rows match these filters."
      }</span>`,
    });

    // Filter changes re-render the LiveView, which updates data-rows; the
    // server also pushes directly after an event that does not re-render.
    this.handleEvent("reports:update_rows", ({ rows: newRows }) => {
      if (this.gridApi && !this.serverSide) {
        this.gridApi.setGridOption("rowData", newRows || []);
      }
    });

    // A report filter changed. The server-side cache holds blocks fetched
    // under the *old* filters, so it has to be dropped — refreshing without
    // purging would leave stale rows above the scroll position.
    this.handleEvent("reports:refresh", () => {
      if (this.gridApi && this.serverSide) {
        this.gridApi.refreshServerSide({ purge: true });
      }
    });
  },

  clientOptions() {
    return {
      rowData: this.parse(this.el.dataset.rows, []),
      pagination: true,
      paginationPageSize: 25,
      paginationPageSizeSelector: [25, 50, 100, 250],
    };
  },

  serverSideOptions() {
    return {
      rowModelType: "serverSide",
      cacheBlockSize: BLOCK_SIZE,
      // Two blocks in flight is enough to scroll smoothly without letting a
      // fast scroll queue a dozen queries against the register.
      maxConcurrentDatasourceRequests: 2,
      // Bounded so a long scroll through a large report cannot grow the tab's
      // memory without limit — the failure this row model exists to avoid.
      maxBlocksInCache: 10,
      blockLoadDebounceMillis: 100,
      serverSideDatasource: {
        getRows: (params) => {
          this.pushEvent(
            "reports:rows",
            { request: params.request },
            (reply) => {
              if (reply && Array.isArray(reply.rows)) {
                params.success({ rowData: reply.rows, rowCount: reply.total });
              } else {
                params.fail();
              }
            }
          );
        },
      },
    };
  },

  updated() {
    if (!this.gridApi) return;

    // Columns change when the user navigates between reports without the hook
    // being torn down, so both halves are reapplied.
    const columns = this.parse(this.el.dataset.columns, null);
    if (columns) {
      this.gridApi.setGridOption(
        "columnDefs",
        columns.map((c) => this.columnDef(c))
      );
    }

    if (this.serverSide) return;

    const rows = this.parse(this.el.dataset.rows, null);
    if (rows) this.gridApi.setGridOption("rowData", rows);
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },

  columnDef(column) {
    const numeric = NUMERIC.includes(column.type);

    const def = {
      headerName: column.label,
      field: column.key,
      headerTooltip: column.hint || undefined,
      type: numeric ? "numericColumn" : undefined,
      // Dates are sent as ISO 8601 strings, which sort correctly as text, so
      // they use the text filter rather than AG Grid's date filter — the date
      // filter would need parsing back into Date objects for no gain over a
      // range that is already constrained by the report's own date filter.
      filter: numeric ? "agNumberColumnFilter" : "agTextColumnFilter",
      valueFormatter: formatters[column.type],
      enableRowGroup: !numeric,
      enableValue: numeric,
      cellClass: numeric ? "ag-right-aligned-cell tabular-nums" : undefined,
    };

    if (numeric) def.aggFunc = "sum";

    return def;
  },

  parse(raw, fallback) {
    if (raw == null) return fallback;
    try {
      return JSON.parse(raw);
    } catch (e) {
      console.warn("ReportsAgGridHook: failed to parse dataset JSON", e);
      return fallback;
    }
  },
};

export default ReportsAgGridHook;
